/** Three human-sized pages for the consented guided route. Navigation always
 * returns to the question heading instead of stranding people mid-page. */

import { useLayoutEffect, useRef, useState } from "react";

import type { SharedBrief } from "@core/index.js";

import { useAnnounce } from "../app/announcer";
import { useStudio } from "../app/store";
import { answerBrief, briefValue, studioQuestion, type StudioQuestion } from "../wiring/questions";

type Group = { title: string; note: string; ids: string[] };

const GROUPS: Group[] = [
  { title: "Where will people see it?", note: "A projector and a phone are not the same room wearing different trousers.", ids: ["viewingContexts", "density"] },
  { title: "How loyal should we be?", note: "Keep the image’s mood, keep only its temperature, or let the palette wander a little.", ids: ["sourceRelationship", "baseMode"] },
  { title: "Any charts doing actual work?", note: "Only answer what the deck needs. Decorative pie charts may leave quietly.", ids: ["dataNeed", "dataCount", "divergingMidpoint"] },
];

const active = (question: StudioQuestion, brief: SharedBrief) => {
  if (question.id === "dataCount") return ["categorical", "sequential", "diverging"].includes(brief.dataNeed ?? "");
  if (question.id === "divergingMidpoint") return brief.dataNeed === "diverging";
  return true;
};

const valid = (question: StudioQuestion, value: unknown) => {
  if (question.kind === "multi") return Array.isArray(value) && value.length > 0;
  if (question.kind === "number") return typeof value === "number" && Number.isInteger(value) && value >= (question.min ?? 2) && value <= (question.max ?? 12);
  return typeof value === "string" && value.trim().length > 0;
};

const ChoiceField = ({ question, value, onChange }: { question: StudioQuestion; value: unknown; onChange: (value: unknown) => void }) => {
  if (question.kind === "single") return (
    <div className="answer-grid" role="radiogroup" aria-label={question.title}>
      {question.options.map((option) => <label className="answer-card" key={option.id} data-selected={value === option.id}><input type="radio" name={question.id} checked={value === option.id} onChange={() => onChange(option.id)} /><span>{option.label}</span></label>)}
    </div>
  );

  if (question.kind === "multi") {
    const selected = Array.isArray(value) ? value as string[] : [];
    return (
      <div className="answer-grid" aria-label={question.title}>
        {question.options.map((option) => {
          const checked = selected.includes(option.id);
          return <label className="answer-card" key={option.id} data-selected={checked}><input type="checkbox" name={question.id} checked={checked} onChange={() => onChange(checked ? selected.filter((id) => id !== option.id) : [...selected, option.id])} /><span>{option.label}</span></label>;
        })}
      </div>
    );
  }

  if (question.kind === "number") {
    const numeric = typeof value === "number" && Number.isFinite(value) ? value : question.min ?? 2;
    const setNumeric = (next: number) => onChange(Math.max(question.min ?? 2, Math.min(question.max ?? 12, next)));
    return (
      <div className="number-choice">
        <div className="number-stepper"><button type="button" aria-label="One fewer" onClick={() => setNumeric(numeric - 1)} disabled={numeric <= (question.min ?? 2)}>−</button><output aria-live="polite">{numeric}</output><button type="button" aria-label="One more" onClick={() => setNumeric(numeric + 1)} disabled={numeric >= (question.max ?? 12)}>+</button></div>
        <div className="number-presets" aria-label="Common counts">{[2, 3, 4, 5, 6, 8, 10, 12].map((count) => <button type="button" key={count} aria-pressed={numeric === count} onClick={() => setNumeric(count)}>{count}</button>)}</div>
      </div>
    );
  }

  const text = typeof value === "string" ? value : "";
  return (
    <div className="text-with-presets">
      <div className="text-presets" aria-label="Common midpoint answers">{["Zero", "Target", "Break-even", "Parity"].map((preset) => <button type="button" key={preset} aria-pressed={text === preset} onClick={() => onChange(preset)}>{preset}</button>)}</div>
      <label className="counted-field"><span className="sr-only">Or type another meaningful midpoint</span><input className="text-input" type="text" placeholder="Or name the meaningful midpoint" maxLength={80} value={text} onChange={(event) => onChange(event.target.value)} /><span>{text.length}/80</span></label>
    </div>
  );
};

export type BriefPageProgress = { page: number; total: number; title: string };

export const QuestionFlow = ({ onDone, onProgress, onBack }: { onDone: () => void; onProgress?: (progress: BriefPageProgress) => void; onBack: () => void }) => {
  const { state, dispatch } = useStudio();
  const announce = useAnnounce();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [page, setPage] = useState(0);
  const group = GROUPS[Math.min(page, GROUPS.length - 1)] ?? GROUPS[0];

  useLayoutEffect(() => {
    if (!group) return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    const frame = window.requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: "auto" }));
    onProgress?.({ page, total: GROUPS.length, title: group.title });
    headingRef.current?.focus({ preventScroll: true });
    return () => window.cancelAnimationFrame(frame);
  }, [group, onProgress, page]);

  if (!group) return null;
  const questions = group.ids.map(studioQuestion).filter((item): item is StudioQuestion => item !== null).filter((item) => active(item, state.brief));
  const pageOkay = questions.every((question) => valid(question, briefValue(state.brief, question.id)));
  const last = page === GROUPS.length - 1;

  const previous = () => page === 0 ? onBack() : setPage((current) => current - 1);
  const next = () => {
    if (!pageOkay) return;
    if (last) {
      announce("Brief complete. Making your palette now.");
      onDone();
    } else setPage((current) => current + 1);
  };

  return (
    <div className="brief-flow" aria-labelledby="brief-group-title">
      <h2 ref={headingRef} tabIndex={-1} className="step-title" id="brief-group-title">{group.title}</h2>
      <p className="step-intro">{group.note}</p>
      <div className="question-stack">{questions.map((question) => <fieldset className="question-block" key={question.id}><legend>{question.title}</legend><p>{question.note}</p><ChoiceField question={question} value={briefValue(state.brief, question.id)} onChange={(answer) => dispatch({ type: "setBrief", brief: answerBrief(state.brief, question.id, answer) })} /></fieldset>)}</div>
      <div className="flow-actions"><button type="button" className="btn" onClick={previous}>Back</button><button type="button" className="btn btn--primary" disabled={!pageOkay} onClick={next}>{last ? "Make my palette" : "Next"}</button>{!pageOkay ? <span className="hint">Answer this page to continue.</span> : null}</div>
    </div>
  );
};
